import { useId } from 'react'
import { MOONBASE_MONOGRAM } from '@solum/experience'

/**
 * The Moonbase maker mark — the "by Moonbase" half of the OnMarble launcher's
 * dual-brand lockup (#2328 brand amendment, ROOT ruling 74071 §1).
 *
 * ── it is the REGISTERED asset, and that is the whole ruling ───────────────
 * *"Use the registered CMS `logo.svg` / governed Moonbase mark already
 * identified by the census. Do not draw another monogram or fall back to typed
 * initials."* So the geometry is imported from `MOONBASE_MONOGRAM` in
 * `@solum/experience`, which is the same path `MOONBASE.logo` composes its
 * markup from — one asset, two readers, and a correction reaches both. Nothing
 * here draws a letterform, and there is deliberately no `initials` fallback: an
 * absent asset is a build failure, not a degraded lockup.
 *
 * ── PLATFORM constant, never `BrandTokens.logo` ───────────────────────────
 * Exactly `MarbleMark`'s argument, one brand over. `BrandTokens.logo` is the
 * DEPLOYMENT's identity — a Coccolare console wears Coccolare's mark in the
 * top-left lockup, correctly. This is the MAKER's identity, and reading it off
 * the brand token would paint the customer's own logo as the "by Moonbase"
 * attribution: a counterfeit variant, which is the one thing the amendment
 * names outright (*"may not present an unattributed or counterfeit variant"*).
 *
 * ── `MoonbaseMark`, not `MoonbaseLogo` ────────────────────────────────────
 * Two reasons, both measured. `apps/portal/src/app.branding.test.ts` is a
 * name-shaped tripwire around the word *logo* as deployment data, and *mark*
 * is what the sibling platform constant is already called.
 *
 * ── the gradient id comes from `useId` ────────────────────────────────────
 * `MOONBASE.logo`'s literal `id="mbg"` is safe inside a data-URI `<img>`, which
 * is its own document. This renders INLINE, so two marks on one page — the
 * collapsed lockup and an About panel, say — would both reference the first
 * one's `<defs>` and the second would paint nothing in some browsers. Same
 * defect `MarbleMark` documents, same remedy.
 */
export function MoonbaseMark({ size = 14, className }: { size?: number; className?: string }) {
  const uid = useId()
  const grad = `mb-g-${uid}`
  return (
    <svg
      viewBox={MOONBASE_MONOGRAM.viewBox}
      width={size} height={Math.round((size * 207) / 354)}
      className={className} aria-hidden="true" focusable="false"
      style={{ fillRule: 'evenodd', clipRule: 'evenodd' }}
    >
      <defs>
        <linearGradient
          id={grad} x1="0" y1="0" x2="1" y2="0"
          gradientUnits="userSpaceOnUse"
          gradientTransform={MOONBASE_MONOGRAM.gradientTransform}
        >
          {MOONBASE_MONOGRAM.stops.map((stop) => (
            <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
          ))}
        </linearGradient>
      </defs>
      <g transform={MOONBASE_MONOGRAM.transform}>
        <path d={MOONBASE_MONOGRAM.path} fill={`url(#${grad})`} />
      </g>
    </svg>
  )
}
